import { Component, type ErrorInfo, type ReactNode } from 'react'
import { RELOAD_WARNING, describeCrash, escalation, type CrashScope } from '../lib/crashcopy'

/**
 * Catches a crash in one part of the app instead of losing the whole thing.
 *
 * WHY THIS EXISTS. React unmounts the entire tree when a render throws and
 * nothing catches it — so until now, one bad value anywhere took the dashboard
 * to a blank white page. No tabs, no header, no way back except a reload she
 * would have to think of herself. For an app whose promise is "nothing happens
 * that you did not do", vanishing is the worst available failure.
 *
 * It must be a class. `componentDidCatch` has no hook equivalent; this is the
 * one place in the codebase where that is not a style choice.
 *
 * ── WHAT IT DOES NOT CATCH, so nobody assumes otherwise ─────────────────────
 *
 * Event handlers, `setTimeout`/`setInterval` callbacks, promise rejections and
 * `async` function bodies. That is most of how this app actually breaks — every
 * `void load()` and every `onClick={() => void approve(...)}` is outside it.
 * `ExportMenu` already carries a comment saying as much and handles its own
 * timer errors locally. This covers render and lifecycle, which is real but is
 * not everything.
 *
 * ── NO WORDING LIVES HERE ───────────────────────────────────────────────────
 *
 * Every sentence comes from `crashcopy.ts`, which is testable. Wording left in
 * this file is wording nothing can check, and it has been wrong twice for
 * exactly that reason.
 */

type Props = {
  children: ReactNode
  /** Named in the sentence she reads: "The Outbox has stopped." */
  where: string
  /**
   * Change this to clear a crash — the pathname plus the router's location key,
   * so pressing the tab you are already on counts too.
   *
   * A prop rather than a `key`, deliberately. Keying the boundary remounts its
   * children on every navigation, and `AgentPanel` holds a half-typed note in
   * component state — so a tab click to check something threw the note away
   * with no warning.
   */
  resetKey?: string
  /** Render nothing at all when crashed, for a strip that is off screen anyway. */
  quiet?: boolean
  /**
   * One line instead of a page-sized card. For the update line, which is one
   * line — a `.board` fallback there pushed the whole roster down the page.
   */
  compact?: boolean
  /** Is anything else left on screen? Changes what she can be told to do. */
  scope?: CrashScope
}

type State = {
  error: unknown
  crashed: boolean
  /** Retry presses **within this failure**, not across the session. */
  attempts: number
  /** The `resetKey` in force at the last render. */
  seen?: string
}

export default class Boundary extends Component<Props, State> {
  state: State = { error: null, crashed: false, attempts: 0 }

  /** Only the error. The sentence is built in `render`, where props exist. */
  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error, crashed: true }
  }

  /**
   * Clearing a crash on navigation, without the double-catch the obvious
   * version had.
   *
   * `componentDidUpdate` comparing `prevProps` looked right and was not: a
   * crash-on-arrival lands in the *same* commit as the `resetKey` change, so it
   * reset the state it had just set, re-rendered the throwing screen and caught
   * it a second time. Two stacks in the console, four under StrictMode, from
   * one crash — and the console is the only diagnostic there is.
   *
   * Comparing against the key recorded at the last render fixes it: on arrival
   * the new key is already recorded before the child throws, so there is
   * nothing to reset.
   */
  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.seen === props.resetKey) return null
    if (state.crashed) return { error: null, crashed: false, attempts: 0, seen: props.resetKey }
    return { seen: props.resetKey }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    // Logged, not swallowed. Nothing collects these yet, and a crash that left
    // no trace at all would be diagnosed by guesswork.
    console.error('crash in', this.props.where, error, info?.componentStack)
  }

  componentDidUpdate() {
    /**
     * A retry that WORKED ends the failure, so the count goes back to nothing.
     *
     * Without this, a successful retry left the counter at one, and the next
     * crash — twenty minutes and one five-minute refresh later — opened with
     * "that has now failed twice, this is not going to fix itself". It had
     * fixed itself, and the message was telling her to stop trying on the first
     * failure of a new incident.
     */
    if (!this.state.crashed && this.state.attempts > 0) this.setState({ attempts: 0 })
  }

  private retry = () => {
    this.setState((s) => ({ error: null, crashed: false, attempts: s.attempts + 1 }))
  }

  render() {
    if (!this.state.crashed) return this.props.children
    if (this.props.quiet) return null

    const { where, compact, scope = 'inside' } = this.props
    const crash = describeCrash(this.state.error, where, scope)
    const more = escalation(this.state.attempts)
    const warning = compact ? RELOAD_WARNING.strip : RELOAD_WARNING.screen

    const buttons = (
      <>
        <button className="btn sm" onClick={this.retry}>
          Try again
        </button>
        {/*
          Second, and not the first thing offered — except when nothing else is
          left on the page, where it is the only way back.
        */}
        <button className={scope === 'alone' ? 'btn sm' : 'btn ghost sm'} onClick={() => window.location.reload()}>
          Reload the page
        </button>
      </>
    )

    if (compact) {
      return (
        <div className="syncstrip bad" role="alert">
          <span className="what">{crash.headline}</span>
          <span className="why">{warning}</span>
          <span className="grow" />
          {buttons}
        </div>
      )
    }

    return (
      <div className="board" role="alert">
        <section className="sec crash">
          {/*
            `h2`, not `h3`. `.sec h3` is a 14px uppercase letter-spaced grey
            LABEL — the style used for the word "NOTES" — so the one sentence
            this whole thing exists to get right rendered as small grey shouting
            with a full stop in it.
          */}
          <h2>{crash.headline}</h2>
          <p>{crash.advice}</p>
          {more && <p className="urgent">{more}</p>}
          <div className="savebar">
            {buttons}
            <span className="status">{warning}</span>
          </div>
          {crash.technical && (
            <p className="hint">
              <strong>For Chris:</strong> {crash.technical} — the component stack is in the
              browser console.
            </p>
          )}
        </section>
      </div>
    )
  }
}
